import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  Loader2,
  Pause,
  Pencil,
  Play,
  RefreshCcw,
  Square,
} from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_VARIANTS = {
  draft: "outline",
  scheduled: "secondary",
  running: "default",
  paused: "secondary",
  completed: "outline",
  cancelled: "destructive",
};

const JOB_STATUSES = [
  "queued", "leased", "dialing", "in_progress",
  "completed", "busy", "no_answer", "failed", "voicemail", "cancelled",
];

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function CampaignControl() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [jobs, setJobs] = useState({ total: 0, jobs: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [page, setPage] = useState(0);
  const limit = 25;

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const params = { limit, offset: page * limit };
      if (statusFilter !== "__all__") params.status = statusFilter;
      const [c, j] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/jobs`, { params }),
      ]);
      setCampaign(c.data);
      setJobs(j.data);
    } catch (err) {
      toastError(err, "Failed to load campaign");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, statusFilter, page]);

  const lifecycle = async (action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(action);
      fetchAll();
    } catch (err) {
      toastError(err, `Failed to ${action}`);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!campaign) return null;

  const stats = campaign.stats || {};
  const total = stats.total || 0;
  const done = (stats.completed || 0) + (stats.failed || 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="campaign-control">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
            <Link to="/campaigns" data-testid="control-back">
              <ChevronLeft className="h-4 w-4" /> Campaigns
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="control-name">
              {campaign.name}
            </h1>
            <Badge variant={STATUS_VARIANTS[campaign.status] || "outline"} data-testid="control-status">
              {campaign.status}
            </Badge>
          </div>
          {campaign.description && (
            <p className="mt-1 text-sm text-muted-foreground">{campaign.description}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchAll} data-testid="control-refresh">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={() => navigate(`/campaigns/${id}`)} data-testid="control-edit">
            <Pencil className="h-4 w-4" /> Edit
          </Button>
          {campaign.status === "draft" && (
            <Button onClick={() => lifecycle("launch")} data-testid="control-launch">
              <Play className="h-4 w-4" /> Launch
            </Button>
          )}
          {campaign.status === "running" && (
            <Button variant="outline" onClick={() => lifecycle("pause")} data-testid="control-pause">
              <Pause className="h-4 w-4" /> Pause
            </Button>
          )}
          {campaign.status === "paused" && (
            <Button onClick={() => lifecycle("resume")} data-testid="control-resume">
              <Play className="h-4 w-4" /> Resume
            </Button>
          )}
          {!["completed", "cancelled"].includes(campaign.status) && (
            <Button variant="outline" onClick={() => lifecycle("cancel")} data-testid="control-cancel">
              <Square className="h-4 w-4" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="rounded-lg border border-border bg-card p-4" data-testid="control-progress">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Progress</span>
          <span className="font-mono text-muted-foreground tabular-nums">{done} / {total} ({pct}%)</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-secondary">
          <div
            className="h-2 rounded-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total || 0} testid="stat-total" />
        <StatCard label="Pending" value={stats.pending || 0} testid="stat-pending" />
        <StatCard label="Dialing" value={stats.dialing || 0} testid="stat-dialing" />
        <StatCard label="Completed" value={stats.completed || 0} testid="stat-completed" />
        <StatCard label="Failed" value={stats.failed || 0} testid="stat-failed" />
        <StatCard label="Busy" value={stats.busy || 0} testid="stat-busy" />
        <StatCard label="No answer" value={stats.no_answer || 0} testid="stat-noanswer" />
        <StatCard label="Voicemail" value={stats.voicemail || 0} testid="stat-voicemail" />
        <StatCard label="Do not call" value={stats.do_not_call || 0} testid="stat-dnc" />
      </div>

      {/* Jobs */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-44" data-testid="control-job-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-muted-foreground">{jobs.total} jobs</span>
        </div>
        {jobs.jobs.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="control-jobs-empty">
            No jobs match this filter.
          </div>
        ) : (
          <Table data-testid="control-jobs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Attempt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Not before</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Hangup cause</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.jobs.map((j) => (
                <TableRow key={j.id} data-testid={`control-job-${j.id}`}>
                  <TableCell className="font-mono text-sm">{j.contact_phone}</TableCell>
                  <TableCell className="text-sm">{j.attempt_number}</TableCell>
                  <TableCell><Badge variant="outline">{j.status}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {j.not_before ? new Date(j.not_before).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {j.result?.started_at ? new Date(j.result.started_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-sm">{j.result?.duration_s != null ? `${j.result.duration_s}s` : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{j.result?.hangup_cause || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {jobs.total > limit && (
          <div className="flex items-center justify-between border-t border-border p-2 text-xs">
            <span>Page {page + 1} of {Math.ceil(jobs.total / limit)}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * limit >= jobs.total} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
